import type React from "react";
import { brand } from "../theme/brand";

// ─── Sync-state types ─────────────────────────────────────────────────────────

export type SyncStatus = "local" | "synced" | "syncing" | "offline";

// ─── Shared resolver ──────────────────────────────────────────────────────────
// Maps the 4-value SyncStatus enum to tooltip text + icon/dot colour.
// Used by AppShell header badge AND AccountSection preview — single source of truth.

export function resolveAccountUiState(syncStatus: SyncStatus): {
  statusText: string;
  color: string;
} {
  switch (syncStatus) {
    case "synced":  return { statusText: "Cloud synced",   color: brand.state.success };
    case "syncing": return { statusText: "Syncing…",       color: brand.state.warning };
    case "offline": return { statusText: "Sync issue",     color: brand.state.danger };
    default:        return { statusText: "Saving locally", color: brand.text.muted };
  }
}

// ─── Supabase → SyncStatus mapper ────────────────────────────────────────────
// Converts raw Supabase auth/sync state to the canonical 4-value enum.
// AccountSection uses this so it shares the same logic as AppShell's syncStatus prop.

export function mapSupabaseToSyncStatus(
  isSignedIn: boolean,
  supabaseStatus: string,
  supabaseError: string | null,
): SyncStatus {
  if (!isSignedIn) return "local";
  if (supabaseError || supabaseStatus === "error") return "offline";
  if (supabaseStatus === "syncing") return "syncing";
  return "synced";
}

// ─── CloudStatusIcon ──────────────────────────────────────────────────────────
// Inline SVG cloud icon whose stroke colour reflects the current sync state.
// "local" renders a cloud-off (cloud + diagonal slash). All others render a plain cloud.
// A <title> element provides a native browser tooltip.

export function CloudStatusIcon({
  syncStatus,
  size = 15,
}: {
  syncStatus: SyncStatus;
  size?: number;
}) {
  const { statusText, color } = resolveAccountUiState(syncStatus);
  const isOff = syncStatus === "local";

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, display: "inline-block", verticalAlign: "middle" }}
      aria-label={statusText}
    >
      <title>{statusText}</title>
      {isOff ? (
        <>
          {/* Cloud-off: partial cloud arcs + slash diagonal */}
          <path d="M20.39 18.39A5 5 0 0 0 18 10h-1.26A8 8 0 0 0 5 9" />
          <path d="M17.8 21.8A8 8 0 1 1 6 6" />
          <line x1="2" y1="2" x2="22" y2="22" />
        </>
      ) : (
        /* Standard cloud */
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      )}
    </svg>
  );
}

// ─── AvatarBubble ─────────────────────────────────────────────────────────────
// Renders the avatar circle: uploaded image → SVG shape → silhouette (guest)
// → initials (authenticated). Priority order matches real header behaviour.

export function AvatarBubble({
  avatarKey,
  initials,
  isAuthenticated,
  size,
}: {
  avatarKey: string;
  initials: string;
  isAuthenticated: boolean;
  size: number;
}) {
  const base: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 999,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  // Any data:image/ URL (uploaded photo or SVG shape preset)
  if (avatarKey && /^data:image\//.test(avatarKey)) {
    return (
      <img
        src={avatarKey}
        alt="avatar"
        style={{ ...base, objectFit: "cover" }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
      />
    );
  }

  // No avatar + not signed in → silhouette
  if (!avatarKey && !isAuthenticated) {
    return (
      <span style={{ ...base, background: brand.background.elevated }}>
        <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62} fill="rgba(255,255,255,0.75)" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M20 21a8 8 0 0 0-16 0" />
        </svg>
      </span>
    );
  }

  // No avatar + signed in → muted slate-blue initials bubble
  if (!avatarKey) {
    return (
      <span style={{ ...base, background: brand.avatar.fallbackBackground, color: brand.avatar.fallbackText, fontWeight: 800, fontSize: size * 0.46 }}>
        {initials}
      </span>
    );
  }

  // Legacy emoji fallback (kept for safety; shouldn't occur with new shape system)
  return (
    <span style={{ ...base, background: "rgba(255,255,255,0.08)", fontSize: size * 0.6 }}>
      {avatarKey}
    </span>
  );
}

// ─── AccountBadge ─────────────────────────────────────────────────────────────
// Compact badge used as the AppShell header pill and the AccountSection preview.
// Layout: [Avatar] [Name] [CloudStatusIcon] [Chevron (optional)]
// The preview passes showChevron={false} and wraps it in a non-interactive container.

export function AccountBadge({
  avatarKey,
  initials,
  displayName,
  syncStatus,
  isAuthenticated,
  showChevron = true,
}: {
  avatarKey: string;
  initials: string;
  displayName: string;
  syncStatus: SyncStatus;
  isAuthenticated: boolean;
  showChevron?: boolean;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        border: `1px solid ${brand.border.strong}`,
        background: "rgba(255,255,255,0.04)",
        borderRadius: 12,
        padding: "6px 10px 6px 6px",
        flexShrink: 0,
      }}
    >
      <AvatarBubble
        avatarKey={avatarKey}
        initials={initials}
        isAuthenticated={isAuthenticated}
        size={28}
      />
      <span
        className="account-badge-name"
        style={{
          fontSize: 13,
          fontWeight: 650,
          color: "rgba(248,250,252,0.95)",
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
          maxWidth: 90,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {displayName || "Guest"}
      </span>
      <CloudStatusIcon syncStatus={syncStatus} size={14} />
      {showChevron && (
        <svg
          viewBox="0 0 10 6"
          style={{ width: 9, height: 5, opacity: 0.35, fill: "white", flexShrink: 0 }}
        >
          <path d="M0 0l5 6 5-6z" />
        </svg>
      )}
    </div>
  );
}
